import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Droplets, SprayCan, CheckCircle2, Zap, Sunrise, CloudRain } from "lucide-react";
import { C, FONT, hexA, card, cardHoverShadow, clamp, mmss } from "../theme";
import { priceLevelColor, priceLevelLabel } from "../lib/price";
import { DAY, hhmm, cheapColumns, blockCostOre, fixedRefCostOre, savingPct } from "../lib/scheduleCost";
import { whyPhrase, blockReason, stripToday } from "../lib/nextRun";
import type { DashboardState, DaySchedule, PriceInfo, PriceLevel, ScheduleBlock, ScheduleZone, Zone, ZoneState, ZoneType } from "../types";

const LABEL_W = 188;
const SUB_INDENT = 16;
const DAY_W = 300; // min px per day before the card scrolls horizontally
const GAP = 12; // label↔track gap; the overlays inset by LABEL_W + GAP
const TRACK_INSET = LABEL_W + GAP;

/* ── Reduced-motion (drop purely decorative motion entirely, not just freeze it) ─*/
function useReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const on = () => setReduced(mq.matches);
    on();
    mq.addEventListener?.("change", on);
    return () => mq.removeEventListener?.("change", on);
  }, []);
  return reduced;
}

/* ── A live now-line: tick from wall-clock, re-baseline when the snapshot moves ──*/
function useLiveNowMin(snapshotNowMin: number) {
  const base = useRef({ wall: Date.now(), min: snapshotNowMin });
  const [nowMin, setNowMin] = useState(snapshotNowMin);
  useEffect(() => {
    base.current = { wall: Date.now(), min: snapshotNowMin };
    setNowMin(snapshotNowMin);
  }, [snapshotNowMin]);
  useEffect(() => {
    const t = setInterval(() => {
      setNowMin(base.current.min + (Date.now() - base.current.wall) / 60000);
    }, 1000);
    return () => clearInterval(t);
  }, []);
  return nowMin;
}

/* ── Horizon scale ───────────────────────────────────────────────────────
   The x-axis is absolute minutes from the start of today across `days`. */
interface Scale {
  days: number;
  total: number;
  X: (m: number) => string;
  W: (min: number) => string;
  grid: string;
}
function makeScale(days: number): Scale {
  const total = Math.max(1, days) * DAY;
  return {
    days,
    total,
    X: (m) => `${(Math.max(0, Math.min(total, m)) / total) * 100}%`,
    W: (min) => `${(Math.max(0, min) / total) * 100}%`,
    grid: `repeating-linear-gradient(90deg, ${hexA(C.ink, 0.04)} 0 1px, transparent 1px ${100 / (days * 4)}%)`,
  };
}

/* ── Zone grouping (unchanged contract) ───────────────────────────────────
   Each zone keeps its own lane, but zones sharing a type + window collapse
   under one labeled header so the repeated "Lawn — " prefix disappears. */
interface ZoneGroup {
  key: string;
  title: string;
  type: ZoneType;
  window: [number, number];
  zones: ScheduleZone[];
}

function splitName(name: string): { prefix: string | null; suffix: string } {
  const i = name.indexOf(" — ");
  return i === -1 ? { prefix: null, suffix: name } : { prefix: name.slice(0, i), suffix: name.slice(i + 3) };
}

function groupZones(zones: ScheduleZone[]): ZoneGroup[] {
  const groups: ZoneGroup[] = [];
  const byKey = new Map<string, ZoneGroup>();
  for (const z of zones) {
    const key = `${z.type}|${z.window[0]}-${z.window[1]}`;
    let g = byKey.get(key);
    if (!g) {
      g = { key, title: "", type: z.type, window: z.window, zones: [] };
      byKey.set(key, g);
      groups.push(g);
    }
    g.zones.push(z);
  }
  for (const g of groups) {
    const prefixes = g.zones.map((z) => splitName(z.name).prefix);
    const shared = prefixes[0] && prefixes.every((p) => p === prefixes[0]) ? prefixes[0] : null;
    g.title = shared ?? (g.type === "drip" ? "Drip beds" : "Sprinklers");
  }
  return groups;
}

const subLabel = (z: ScheduleZone, g: ZoneGroup) => {
  const { prefix, suffix } = splitName(z.name);
  return prefix && prefix === g.title ? suffix : z.name;
};

/* ── Per-zone live status pip ─────────────────────────────────────────────*/
const STATE_TEXT: Record<ZoneState, string> = {
  idle: "Idle — above refill",
  watering: "Watering now",
  queued: "Queued",
  "wind-hold": "Held — wind over limit",
  "freeze-hold": "Held — freeze",
  "price-hold": "Waiting for cheaper power",
  "window-hold": "Outside its window",
  off: "Disabled",
};

function pipStyle(s?: ZoneState): React.CSSProperties {
  const b: React.CSSProperties = { width: 7, height: 7, borderRadius: 99, flexShrink: 0 };
  switch (s) {
    case "watering":
      return { ...b, background: C.sea };
    case "queued":
      return { ...b, background: hexA(C.sea, 0.45) };
    case "wind-hold":
    case "freeze-hold":
    case "price-hold":
    case "window-hold":
      return { ...b, background: C.beacon };
    case "off":
      return { ...b, background: "transparent", boxShadow: `inset 0 0 0 1.5px ${hexA(C.textLow, 0.7)}` };
    default:
      return { ...b, background: hexA(C.textLow, 0.35) };
  }
}

/* ── Derivations ──────────────────────────────────────────────────────────*/
const zoneColorOf = (t: ZoneType) => (t === "drip" ? C.sage : C.sea);

// Pricing math (bandV, cheapColumns, blockCostOre, fixedRefCostOre, savingPct) and
// the next-run narrative (whyPhrase, blockReason, stripToday, cheapLevel) now live in
// src/lib/scheduleCost.ts and src/lib/nextRun.ts — pure, shared, and unit-tested.

const countdown = (mins: number) => {
  const m = Math.max(0, Math.round(mins));
  return m >= 60 ? `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, "0")}m` : `${m}m`;
};

/* ── Hover popover state (built at hover-time, decoupled from data) ────────*/
interface PopLine {
  t: string;
  c?: string;
  mono?: boolean;
}
/** What to light up on the timeline while a block is hovered — the constraints that
 *  produced its slot. The chosen column rises full-height to the price ridge, so the
 *  alignment ("cheap power here") becomes the explanation, spelled out rather than implied. */
interface HoverFocus {
  windowAbs: [number, number]; // the allowed window the planner could have used (absolute min)
  chosenStart: number; // the slot it actually picked (absolute min)
  chosenDur: number;
  accent: string;
  shaft?: { startMin: number; durMin: number }; // the cheap-power column it landed in, if any
  sunriseMin?: number | null; // absolute sunrise to emphasize (spray, when it finishes near it)
}
interface Hover {
  x: number;
  top: number;
  bottom: number;
  title: string;
  dot?: string;
  why?: string;
  lines: PopLine[];
  focus?: HoverFocus;
}
type HoverContent = Omit<Hover, "x" | "top" | "bottom">;

/* ── Shared label cell (price row + group headers) ────────────────────────*/
function LabelCell({ accent, icon, title, sub, subColor }: { accent: string; icon: React.ReactNode; title: string; sub: string; subColor?: string }) {
  return (
    <div style={{ width: LABEL_W, flexShrink: 0, minWidth: 0, display: "flex", alignItems: "center", gap: 9 }}>
      <span style={{ width: 22, height: 22, borderRadius: 7, background: hexA(accent, 0.13), display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{icon}</span>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 12.5, fontWeight: 600, color: C.ink, lineHeight: 1.15, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{title}</div>
        <div className="mono" style={{ fontFamily: FONT.mono, fontSize: 9.5, lineHeight: 1.3, color: subColor ?? C.textLow, whiteSpace: "nowrap" }}>{sub}</div>
      </div>
    </div>
  );
}

/* ── The day axis: titles + dates, hour ticks, dividers, live flag, sun chips ─*/
function DayAxis({ sc, schedule, nowMin, reduced }: { sc: Scale; schedule: DaySchedule; nowMin: number; reduced: boolean }) {
  const days = Array.from({ length: sc.days }, (_, d) => d);
  const sr = schedule.sunriseMin;
  const t = nowMin <= 40 ? "translateX(0)" : "translateX(-50%)";
  return (
    <div style={{ display: "flex", alignItems: "flex-end", gap: GAP, height: 46 }}>
      <div style={{ width: LABEL_W, flexShrink: 0 }} />
      <div style={{ position: "relative", flex: 1, height: "100%" }}>
        {days.map((d) => (
          <div key={`t${d}`} style={{ position: "absolute", left: sc.X(d * DAY + DAY / 2), transform: "translateX(-50%)", top: 0, textAlign: "center" }}>
            <div style={{ fontSize: 12.5, fontWeight: 700, color: d === 0 ? C.ink : C.textMid, lineHeight: 1.1 }}>{schedule.dayLabels[d]}</div>
            <div className="mono" style={{ fontFamily: FONT.mono, fontSize: 9.5, color: C.textLow }}>{schedule.dayDates[d]}</div>
          </div>
        ))}
        {days.flatMap((d) =>
          [6, 12, 18].map((h) => (
            <span key={`h${d}-${h}`} className="mono" style={{ position: "absolute", left: sc.X(d * DAY + h * 60), bottom: 0, transform: "translateX(-50%)", fontFamily: FONT.mono, fontSize: 9, color: C.muted }}>
              {String(h).padStart(2, "0")}
            </span>
          )),
        )}
        {/* sunrise chip on the baseline, aligned to the line below */}
        {sr != null &&
          days.map((d) => (
            <span
              key={`sun${d}`}
              className="mono"
              style={{
                position: "absolute", left: sc.X(d * DAY + sr), bottom: 14, transform: "translateX(-50%)",
                display: "inline-flex", alignItems: "center", gap: 3, padding: "1px 5px", borderRadius: 99,
                background: C.paper, border: `1px solid ${hexA(C.beacon, 0.35)}`, color: C.beaconDeep,
                fontFamily: FONT.mono, fontSize: 8.5, whiteSpace: "nowrap", zIndex: 2,
              }}
            >
              <Sunrise size={10} style={{ color: C.beacon }} />
              {hhmm(sr)}
            </span>
          ))}
        {Array.from({ length: sc.days - 1 }, (_, i) => (
          <div key={`d${i}`} style={{ position: "absolute", left: sc.X((i + 1) * DAY), top: 2, bottom: 0, width: 0, borderLeft: `1px dashed ${hexA(C.ink, 0.16)}` }} />
        ))}
        <span
          className="mono"
          style={{
            position: "absolute", left: sc.X(nowMin), bottom: 0, transform: t, zIndex: 6,
            fontFamily: FONT.mono, fontSize: 9.5, fontWeight: 700, letterSpacing: 0.2,
            color: C.paper, background: C.ink, padding: "1.5px 5px", borderRadius: 5,
            whiteSpace: "nowrap", boxShadow: "0 1px 3px rgba(40,30,12,.3)", pointerEvents: "none",
          }}
        >
          {hhmm(nowMin)}
        </span>
        {!reduced && (
          <span className="now-pulse" style={{ position: "absolute", left: sc.X(nowMin), bottom: 18, transform: "translateX(-50%)", width: 6, height: 6, borderRadius: 99, background: C.ink, zIndex: 6, pointerEvents: "none" }} />
        )}
      </div>
    </div>
  );
}

/* ── Power-price ridgeline (the curve the watering blocks sit beneath) ─────*/
const PRICE_H = 52;
function PriceRow({ sc, schedule, price }: { sc: Scale; schedule: DaySchedule; price?: PriceInfo }) {
  const bands = schedule.priceBands;
  const peak = Math.max(0.1, ...bands.map((b) => b.v));
  const accent = price ? priceLevelColor(price.level) : C.sea;
  const X = (m: number) => (Math.max(0, Math.min(sc.total, m)) / sc.total) * 1000;
  const Y = (v: number) => PRICE_H - (4 + 40 * (v / peak));

  // Step-area subpaths, broken wherever the bands aren't contiguous (unpriced day).
  const runs: { startMin: number; durMin: number; v: number; level: PriceLevel }[][] = [];
  let run: typeof runs[number] = [];
  for (const b of bands) {
    if (run.length && b.startMin !== run[run.length - 1].startMin + run[run.length - 1].durMin) {
      runs.push(run);
      run = [];
    }
    run.push(b);
  }
  if (run.length) runs.push(run);

  const meanY = price && price.mean > 0 ? Y(price.mean) : null;

  return (
    <div style={{ display: "flex", alignItems: "center", gap: GAP, height: PRICE_H }}>
      <LabelCell
        accent={accent}
        icon={<Zap size={13} style={{ color: accent }} />}
        title="Power · SE4"
        sub={price?.now != null ? `SE4 spot · ${priceLevelLabel(price.level).toLowerCase()}` : "live"}
        subColor={price ? accent : undefined}
      />
      <div style={{ position: "relative", flex: 1, height: "100%" }}>
        <svg width="100%" height="100%" viewBox={`0 0 1000 ${PRICE_H}`} preserveAspectRatio="none" style={{ display: "block", overflow: "visible" }}>
          <defs>
            <linearGradient id="priceFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={hexA(C.coral, 0.2)} />
              <stop offset="50%" stopColor={hexA(C.sea, 0.1)} />
              <stop offset="100%" stopColor={hexA(C.sageDeep, 0.16)} />
            </linearGradient>
          </defs>
          {runs.map((r, ri) => {
            let d = `M ${X(r[0].startMin)},${PRICE_H}`;
            for (const b of r) {
              d += ` L ${X(b.startMin)},${Y(b.v)} L ${X(b.startMin + b.durMin)},${Y(b.v)}`;
            }
            d += ` L ${X(r[r.length - 1].startMin + r[r.length - 1].durMin)},${PRICE_H} Z`;
            return <path key={ri} d={d} fill="url(#priceFill)" />;
          })}
          {meanY != null && <line x1="0" y1={meanY} x2="1000" y2={meanY} stroke={hexA(C.ink, 0.22)} strokeWidth={1} strokeDasharray="3 4" vectorEffect="non-scaling-stroke" />}
          {bands.map((b, i) => (
            <line
              key={i}
              x1={X(b.startMin)}
              y1={Y(b.v)}
              x2={X(b.startMin + b.durMin)}
              y2={Y(b.v)}
              stroke={priceLevelColor(b.level)}
              strokeWidth={2}
              strokeLinecap="round"
              vectorEffect="non-scaling-stroke"
            />
          ))}
        </svg>
        {meanY != null && (
          <span className="mono" style={{ position: "absolute", right: 2, top: 2, fontFamily: FONT.mono, fontSize: 8, color: hexA(C.ink, 0.4) }}>
            kr {price!.mean.toFixed(2)} avg
          </span>
        )}
        {/* faint per-band column tint for vertical tracing */}
        {bands.map((b, i) => {
          const intensity = 0.1 + 0.34 * Math.min(1, b.v / peak);
          return <div key={`t${i}`} style={{ position: "absolute", left: sc.X(b.startMin), width: sc.W(b.durMin), top: 0, bottom: 0, background: hexA(priceLevelColor(b.level), intensity), opacity: 0.5, pointerEvents: "none" }} />;
        })}
        {/* unpriced future days */}
        {schedule.priceDays.map((has, d) =>
          has ? null : (
            <div
              key={`ph${d}`}
              style={{
                position: "absolute", left: sc.X(d * DAY), width: sc.W(DAY), top: 0, bottom: 0,
                background: `repeating-linear-gradient(45deg, ${hexA(C.textLow, 0.07)} 0 5px, transparent 5px 11px)`,
                display: "flex", alignItems: "center", justifyContent: "center",
              }}
            >
              <span style={{ fontSize: 9.5, color: C.textLow, background: hexA(C.paper, 0.8), padding: "1px 6px", borderRadius: 5, whiteSpace: "nowrap" }}>
                {d === 1 ? "prices ~13:00" : "not published yet"}
              </span>
            </div>
          ),
        )}
      </div>
    </div>
  );
}

/* ── Cross-row overlays (behind / in front of the lanes) ───────────────────*/
function OverlayWash({ sc, schedule, shafts, paused, reduced, hover }: { sc: Scale; schedule: DaySchedule; shafts: { startMin: number; durMin: number }[]; paused: boolean; reduced: boolean; hover: Hover | null }) {
  const sr = schedule.sunriseMin;
  const days = Array.from({ length: sc.days }, (_, d) => d);
  const f = hover?.focus;
  return (
    <div style={{ position: "absolute", left: TRACK_INSET, right: 0, top: 0, bottom: 0, zIndex: 0, pointerEvents: "none" }}>
      {paused && (
        <div className={reduced ? undefined : "rain-drift"} style={{ position: "absolute", inset: 0, background: `repeating-linear-gradient(72deg, ${hexA(C.ice, 0.1)} 0 1px, transparent 1px 7px)`, backgroundSize: "auto 14px" }} />
      )}
      {/* cheap-power shafts — drip blocks land inside these */}
      {shafts.map((s, i) => (
        <div key={`sh${i}`} style={{ position: "absolute", left: sc.X(s.startMin), width: sc.W(s.durMin), top: 0, bottom: 0, background: `linear-gradient(180deg, ${hexA(C.sageDeep, 0)} 0, ${hexA(C.sageDeep, 0.1)} 12%, ${hexA(C.sageDeep, 0.06)} 100%)`, borderLeft: `1px solid ${hexA(C.sageDeep, 0.18)}`, borderRight: `1px solid ${hexA(C.sageDeep, 0.18)}` }}>
          <span className="mono" style={{ position: "absolute", top: 2, left: 4, fontFamily: FONT.mono, fontSize: 8.5, color: C.sageDeep, whiteSpace: "nowrap" }}>↓ cheapest</span>
        </div>
      ))}
      {/* dawn wash before each sunrise — spray finishes inside it */}
      {sr != null &&
        days.map((d) => (
          <div key={`dawn${d}`} style={{ position: "absolute", left: sc.X(d * DAY + sr - 90), width: sc.W(90), top: 0, bottom: 0, background: `linear-gradient(90deg, transparent, ${hexA(C.beacon, 0.1)})` }} />
        ))}

      {/* ── Causality reveal: while a block is hovered, light up the constraints that
            produced its slot. "Alignment is the explanation" — said out loud. ── */}
      {f && (
        <div className={reduced ? undefined : "overlay-in"} style={{ position: "absolute", inset: 0 }}>
          {/* the whole window it was free to use */}
          <div style={{ position: "absolute", left: sc.X(f.windowAbs[0]), width: sc.W(f.windowAbs[1] - f.windowAbs[0]), top: 0, bottom: 0, background: hexA(f.accent, 0.05), borderLeft: `1px dashed ${hexA(f.accent, 0.3)}`, borderRight: `1px dashed ${hexA(f.accent, 0.3)}` }} />
          {/* the cheap-power column it landed in, brightened */}
          {f.shaft && <div style={{ position: "absolute", left: sc.X(f.shaft.startMin), width: sc.W(f.shaft.durMin), top: 0, bottom: 0, background: hexA(C.sageDeep, 0.1) }} />}
          {/* the slot it actually chose — projected full-height up to the price ridge */}
          <div style={{ position: "absolute", left: sc.X(f.chosenStart), width: sc.W(f.chosenDur), minWidth: 3, top: 0, bottom: 0, background: hexA(f.accent, 0.17), borderLeft: `1.5px solid ${hexA(f.accent, 0.6)}`, borderRight: `1.5px solid ${hexA(f.accent, 0.6)}` }} />
        </div>
      )}
    </div>
  );
}

function OverlayLines({ sc, schedule, nowMin, hover, paused }: { sc: Scale; schedule: DaySchedule; nowMin: number; hover: Hover | null; paused: boolean }) {
  const sr = schedule.sunriseMin;
  const days = Array.from({ length: sc.days }, (_, d) => d);
  return (
    <div style={{ position: "absolute", left: TRACK_INSET, right: 0, top: 0, bottom: 0, zIndex: 6, pointerEvents: "none" }}>
      {/* day dividers */}
      {Array.from({ length: sc.days - 1 }, (_, i) => (
        <div key={`d${i}`} style={{ position: "absolute", left: sc.X((i + 1) * DAY), top: 0, bottom: 0, width: 0, borderLeft: `1px dashed ${hexA(C.ink, 0.14)}` }} />
      ))}
      {/* sunrise lines — brighten the one a hovered spray block is racing to beat */}
      {sr != null &&
        days.map((d) => {
          const lit = hover?.focus?.sunriseMin === d * DAY + sr;
          return <div key={`sr${d}`} style={{ position: "absolute", left: sc.X(d * DAY + sr), top: 0, bottom: 0, width: 0, borderLeft: `1.5px solid ${hexA(C.beacon, lit ? 0.95 : 0.5)}` }} />;
        })}
      {/* now-line */}
      <div style={{ position: "absolute", left: sc.X(nowMin), top: 0, bottom: 0, width: 0, borderLeft: `1.5px solid ${paused ? hexA(C.ink, 0.4) : C.ink}` }} />
    </div>
  );
}

/* ── Group header (window meaning text removed — hero + popover carry it) ───*/
function GroupHeader({ sc, group }: { sc: Scale; group: ZoneGroup }) {
  const [ws, we] = group.window;
  const accent = zoneColorOf(group.type);
  const Icon = group.type === "drip" ? Droplets : SprayCan;
  const n = group.zones.length;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: GAP, height: 30, marginTop: 6, position: "relative", zIndex: 1 }}>
      <LabelCell accent={accent} icon={<Icon size={13} style={{ color: accent }} />} title={group.title} sub={`${hhmm(ws)}–${hhmm(we)} · ${n} ${n === 1 ? "zone" : "zones"}`} />
      <div style={{ position: "relative", flex: 1, height: "100%" }}>
        {Array.from({ length: sc.days }, (_, d) => (
          <div key={d} style={{ position: "absolute", left: sc.X(d * DAY + ws), width: sc.W(we - ws), top: 3, bottom: 3, background: hexA(accent, d === 0 ? 0.1 : 0.06), border: `1px solid ${hexA(accent, d === 0 ? 0.22 : 0.14)}`, borderRadius: 6 }} />
        ))}
      </div>
    </div>
  );
}

/* ── One zone lane ────────────────────────────────────────────────────────*/
function SubLane({
  sc, schedule, shafts, zone, group, live, paused, reduced, onShow, onHide,
}: {
  sc: Scale; schedule: DaySchedule; shafts: { startMin: number; durMin: number }[]; zone: ScheduleZone; group: ZoneGroup; live?: Zone; paused: boolean; reduced: boolean;
  onShow: (el: HTMLElement, c: HoverContent) => void; onHide: () => void;
}) {
  const [ws, we] = zone.window;
  const accent = zoneColorOf(group.type);
  const blocks = schedule.blocks.filter((b) => b.zoneId === zone.id);
  const zState = live?.state;
  const none = zone.nextRunMin == null;
  const soon = zone.nextRunMin != null && zone.nextRunMin < DAY;
  const nextColor = none ? hexA(C.textLow, 0.55) : soon ? accent : C.textMid;

  const labelContent = (): HoverContent => {
    const lines: PopLine[] = [{ t: zState ? STATE_TEXT[zState] : "—", c: zState === "watering" ? C.seaDeep : C.textMid }];
    if (zState === "watering" && live?.remaining != null) lines.push({ t: `${live.flow.toFixed(1)} L/min · ${mmss(live.remaining)} left`, mono: true, c: C.textMid });
    lines.push({ t: none ? "no run forecast" : `next · ${zone.nextRunLabel}`, mono: true, c: nextColor });
    return { title: subLabel(zone, group), dot: pipDot(zState), lines };
  };

  return (
    <div style={{ display: "flex", alignItems: "center", gap: GAP, height: 40, position: "relative", zIndex: 1 }}>
      <div
        tabIndex={0}
        role="button"
        className="sched-hit"
        aria-label={`${zone.name}. ${zState ? STATE_TEXT[zState] : ""}. ${none ? "no run forecast" : "next " + zone.nextRunLabel}`}
        onMouseEnter={(e) => onShow(e.currentTarget, labelContent())}
        onMouseLeave={onHide}
        onFocus={(e) => onShow(e.currentTarget, labelContent())}
        onBlur={onHide}
        style={{ width: LABEL_W, flexShrink: 0, minWidth: 0, paddingLeft: SUB_INDENT, display: "flex", flexDirection: "column", justifyContent: "center", gap: 1, borderRadius: 6, cursor: "default" }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span className={zState === "watering" && !reduced ? "dot-pulse" : undefined} style={pipStyle(zState)} />
          <span style={{ fontSize: 12.5, color: C.textMid, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{subLabel(zone, group)}</span>
        </div>
        <span className="mono" style={{ paddingLeft: 15, fontFamily: FONT.mono, fontSize: 9.5, color: nextColor, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {none ? "no run forecast" : `next · ${zone.nextRunLabel}`}
        </span>
      </div>
      <div style={{ position: "relative", flex: 1, height: "100%" }}>
        {Array.from({ length: sc.days }, (_, d) => (
          <div key={d} style={{ position: "absolute", left: sc.X(d * DAY + ws), width: sc.W(we - ws), top: 0, bottom: 0, background: hexA(accent, 0.05), borderRadius: 5 }} />
        ))}
        {blocks.map((b, i) => (
          <Block key={`${b.zoneId}#${Math.floor(b.startMin / DAY)}#${i}`} sc={sc} schedule={schedule} shafts={shafts} b={b} zone={zone} group={group} live={live} paused={paused} reduced={reduced} onShow={onShow} onHide={onHide} />
        ))}
      </div>
    </div>
  );
}

const pipDot = (s?: ZoneState) =>
  s === "watering" ? C.sea : s === "queued" ? hexA(C.sea, 0.6) : s && s.endsWith("-hold") ? C.beacon : hexA(C.textLow, 0.5);

function Block({
  sc, schedule, shafts, b, zone, group, live, paused, reduced, onShow, onHide,
}: {
  sc: Scale; schedule: DaySchedule; shafts: { startMin: number; durMin: number }[]; b: ScheduleBlock; zone: ScheduleZone; group: ZoneGroup; live?: Zone; paused: boolean; reduced: boolean;
  onShow: (el: HTMLElement, c: HoverContent) => void; onHide: () => void;
}) {
  const zoneColor = zoneColorOf(group.type);
  const progress = b.kind === "watering" && live?.total ? clamp(1 - (live.remaining ?? 0) / live.total, 0, 1) : null;

  const base: React.CSSProperties = {
    position: "absolute", left: sc.X(b.startMin), width: sc.W(b.durMin), minWidth: 6, top: 4, bottom: 4, borderRadius: 5, overflow: "hidden",
    display: "flex", alignItems: "center", justifyContent: "center", cursor: "default",
    transition: reduced ? undefined : "left .55s cubic-bezier(.2,.8,.2,1), width .55s cubic-bezier(.2,.8,.2,1), background-color .4s ease",
  };

  let style: React.CSSProperties;
  if (b.kind === "done") style = { ...base, background: hexA(C.textLow, 0.2), color: C.textMid, border: `1px solid ${hexA(C.textLow, 0.28)}` };
  else if (b.kind === "watering") style = { ...base, background: hexA(C.seaDeep, 0.85), boxShadow: `0 0 0 2px ${hexA(C.sea, 0.25)}, 0 2px 6px -1px rgba(40,30,12,.35)` };
  else style = { ...base, background: b.est ? hexA(zoneColor, 0.5) : zoneColor, border: `1px ${b.est ? "dashed" : "solid"} ${zoneColor}`, opacity: paused ? 0.5 : 1, boxShadow: "0 1px 3px -1px rgba(40,30,12,.3)" };

  const content = (): HoverContent => {
    const day = Math.floor(b.startMin / DAY);
    const lines: PopLine[] = [{ t: `${hhmm(b.startMin)}–${hhmm(b.startMin + b.durMin)} · ${b.durMin} min`, mono: true, c: C.textMid }];
    if (live && live.flow > 0 && b.kind === "watering") lines.push({ t: `~${Math.round(live.flow * b.durMin)} L applied`, mono: true, c: C.textMid });
    if (!schedule.priceDays[day]) {
      lines.push({ t: "price not published yet", mono: true, c: C.textLow });
    } else {
      const ore = blockCostOre(schedule, b);
      const ref = fixedRefCostOre(schedule, b);
      if (ore != null && ref != null && ref > ore) lines.push({ t: `≈${ore} öre · saves ${ref - ore} vs 06:00`, mono: true, c: C.sageDeep });
      else if (ore != null) lines.push({ t: `≈${ore} öre power`, mono: true, c: C.textMid });
      if (b.level) lines.push({ t: `${priceLevelLabel(b.level)} power`, c: priceLevelColor(b.level) });
    }
    const focus: HoverFocus = {
      windowAbs: [day * DAY + zone.window[0], day * DAY + zone.window[1]],
      chosenStart: b.startMin,
      chosenDur: b.durMin,
      accent: zoneColor,
      shaft: shafts.find((s) => b.startMin >= s.startMin && b.startMin < s.startMin + s.durMin),
      sunriseMin: group.type === "spray" && schedule.sunriseMin != null ? day * DAY + schedule.sunriseMin : null,
    };
    return { title: zone.name, dot: b.level ? priceLevelColor(b.level) : zoneColor, why: blockReason(schedule, b, group.type), lines, focus };
  };

  return (
    <div
      tabIndex={0}
      role="button"
      aria-label={`${zone.name}, ${schedule.dayLabels[Math.floor(b.startMin / DAY)] ?? ""} ${hhmm(b.startMin)} for ${b.durMin} minutes, ${b.kind}${b.level ? `, ${priceLevelLabel(b.level).toLowerCase()} power` : ""}`}
      className={`sched-hit${reduced ? "" : " replan-in"}`}
      onMouseEnter={(e) => onShow(e.currentTarget, content())}
      onMouseLeave={onHide}
      onFocus={(e) => onShow(e.currentTarget, content())}
      onBlur={onHide}
      style={style}
    >
      {b.kind === "watering" && (
        <>
          <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: `${(progress ?? 1) * 100}%`, background: `linear-gradient(90deg, ${C.seaDeep}, ${C.ice})`, transition: reduced ? undefined : "width .9s linear" }} />
          {!reduced && <div className="sheen" style={{ background: "linear-gradient(105deg, transparent, rgba(255,255,255,.32), transparent)" }} />}
          {progress != null && <span style={{ position: "absolute", left: `calc(${progress * 100}% - 3px)`, top: "50%", marginTop: -3, width: 6, height: 6, borderRadius: 99, background: "#fff", boxShadow: "0 0 4px rgba(255,255,255,.8)" }} />}
        </>
      )}
      {b.kind === "done" && <CheckCircle2 size={10} style={{ position: "relative" }} />}
      {b.kind === "planned" && b.level && <span style={{ position: "relative", width: 5, height: 5, borderRadius: 99, background: priceLevelColor(b.level), boxShadow: "0 0 0 1px rgba(255,255,255,.6)" }} />}
    </div>
  );
}

/* ── Hover popover ─────────────────────────────────────────────────────────*/
function BlockPopover({ hover, reduced }: { hover: Hover; reduced: boolean }) {
  const W = 208;
  const flip = hover.top < 120; // not enough room above → drop below
  return (
    <div
      aria-hidden="true"
      className={reduced ? undefined : "overlay-in"}
      style={{
        ...card, boxShadow: cardHoverShadow, position: "absolute", zIndex: 40, width: W, padding: "10px 12px", borderRadius: 12, pointerEvents: "none",
        left: hover.x,
        top: flip ? hover.bottom + 8 : hover.top - 8,
        transform: flip ? "translateX(-50%)" : "translate(-50%, -100%)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: hover.why ? 6 : 5 }}>
        {hover.dot && <span style={{ width: 7, height: 7, borderRadius: 99, background: hover.dot, flexShrink: 0 }} />}
        <span style={{ fontSize: 12.5, fontWeight: 600, color: C.ink }}>{hover.title}</span>
      </div>
      {hover.why && (
        <div style={{ fontSize: 12, lineHeight: 1.4, color: C.ink, marginBottom: 7, paddingLeft: 8, borderLeft: `2px solid ${hexA(hover.dot ?? C.sage, 0.55)}` }}>
          {hover.why}
        </div>
      )}
      {hover.lines.map((l, i) => (
        <div key={i} className={l.mono ? "mono" : undefined} style={{ fontFamily: l.mono ? FONT.mono : undefined, fontSize: l.mono ? 10.5 : 11.5, color: l.c ?? C.textMid, lineHeight: 1.5 }}>
          {l.t}
        </div>
      ))}
    </div>
  );
}

/* ── Answer-first hero ─────────────────────────────────────────────────────*/
function HeroStrip({ state, reduced }: { state: DashboardState; reduced: boolean }) {
  const schedule = state.schedule!;
  const price = state.price;
  const watering = state.zones.filter((z) => z.state === "watering");
  const held = state.zones.filter((z) => z.state.endsWith("-hold"));
  // Earliest *upcoming* run — same ordering as computeNextRun(), so the Schedule
  // hero and the Overview hero never name different next zones.
  const nextZone = schedule.zones
    .filter((z) => z.nextRunMin != null && (z.nextRunMin as number) >= Math.floor(schedule.nowMin))
    .sort((a, b) => (a.nextRunMin as number) - (b.nextRunMin as number))[0];

  let accent: string = C.ink;
  let line1 = "";
  let line2 = "";
  let pulse = false;

  if (state.paused) {
    accent = C.sea;
    line1 = "Paused — rain delay";
    line2 = "Plan held while rain does the watering. Resumes when soil drops below its refill point.";
  } else if (watering.length) {
    accent = C.seaDeep;
    pulse = true;
    const z = watering.slice().sort((a, b) => (a.remaining ?? 1e9) - (b.remaining ?? 1e9))[0];
    line1 = watering.length > 1 ? `Watering now — ${watering.length} zones` : `Watering now — ${z.name}`;
    const lvl = price ? `${priceLevelLabel(price.level).toLowerCase()} power` : "";
    line2 = z.remaining != null && z.total ? `${z.flow.toFixed(1)} L/min · ${mmss(z.remaining)} left of ${Math.round(z.total / 60)} min${lvl ? ` · ${lvl}` : ""}` : `${z.flow.toFixed(1)} L/min flowing${lvl ? ` · ${lvl}` : ""}`;
  } else if (held.length && nextZone && (nextZone.nextRunMin as number) - schedule.nowMin <= 90) {
    accent = C.beacon;
    line1 = "Holding — cheaper power soon";
    const kind = held[0].state;
    line2 =
      kind === "wind-hold" ? "Wind too high for spray — retrying when it eases."
        : kind === "freeze-hold" ? "Freeze hold — protecting the lines."
          : kind === "window-hold" ? `Outside the watering window — next ${nextZone.nextRunLabel}.`
            : `Waiting for the cheap window — next ${nextZone.nextRunLabel}.`;
  } else if (nextZone) {
    accent = C.ink;
    line1 = `All quiet · next ${splitName(nextZone.name).suffix} in ${countdown((nextZone.nextRunMin as number) - schedule.nowMin)}`;
    line2 = whyPhrase(schedule, price, nextZone);
  } else {
    accent = C.ink;
    line1 = "All zones above refill";
    line2 = "No watering needed — soil is wet enough across every zone. Re-checks each hour against the ET₀ forecast.";
  }

  // Stats — the savings stat carries the whole value proposition, so it names its
  // basis and stays confident when there's simply nothing to shift.
  const kpiSave = state.kpis.find((k) => k.label === "Saved vs fixed schedule");
  const savePctNum = kpiSave ? Number(kpiSave.value) : savingPct(schedule);
  const savePctVal = savePctNum != null && !Number.isNaN(savePctNum) ? `~${savePctNum}%` : null;
  const saveStrong = savePctNum != null && savePctNum >= 5;
  const saveLabel = saveStrong ? "cheaper vs 06:00" : "already cheap";
  const saveTitle = kpiSave
    ? "Power cost vs running the same minutes at a fixed 06:00 start, this week — rain- and price-aware."
    : "Power cost vs running the same minutes at a fixed 06:00 start, across the planned horizon.";
  const showPower = !!price?.enabled;

  return (
    <div className="card-in" style={{ ...card, position: "relative", overflow: "hidden", padding: "18px 22px", display: "flex", alignItems: "center", gap: 24, flexWrap: "wrap", marginBottom: 16 }}>
      <span className={pulse && !reduced ? "dot-pulse" : undefined} style={{ position: "absolute", left: 0, top: 14, bottom: 14, width: 4, borderRadius: 99, background: accent }} />
      <div style={{ flex: "1 1 380px", minWidth: 280, paddingLeft: 10 }}>
        <div className="serif" style={{ fontFamily: FONT.serif, fontSize: 26, lineHeight: 1.12, fontWeight: 500, color: accent }}>{line1}</div>
        <div style={{ fontSize: 13, color: C.textMid, marginTop: 4, maxWidth: "54ch" }}>{line2}</div>
      </div>
      <div style={{ display: "flex", alignItems: "stretch", gap: 0, flexWrap: "wrap" }}>
        <Stat label="Next run" value={nextZone ? stripToday(nextZone.nextRunLabel) : "—"} mono />
        {showPower && (
          <Stat
            label={price?.ok && price.now != null ? priceLevelLabel(price.level).toUpperCase() : "POWER"}
            value={price?.ok && price.now != null ? price.now.toFixed(2) : "n/a"}
            unit={price?.ok && price.now != null ? "kr" : undefined}
            color={price?.ok && price.now != null ? priceLevelColor(price.level) : C.textLow}
            divider
          />
        )}
        {savePctVal && <Stat label={saveLabel} value={savePctVal} color={C.sageDeep} divider title={saveTitle} />}
      </div>
    </div>
  );
}

function Stat({ label, value, unit, color, mono, divider, title }: { label: string; value: string; unit?: string; color?: string; mono?: boolean; divider?: boolean; title?: string }) {
  return (
    <div title={title} style={{ display: "flex", flexDirection: "column", justifyContent: "center", paddingLeft: divider ? 18 : 0, marginLeft: divider ? 18 : 0, borderLeft: divider ? `1px solid ${C.lineSoft}` : undefined }}>
      <div className={mono ? "mono tnum" : "serif tnum"} style={{ fontFamily: mono ? FONT.mono : FONT.serif, fontSize: mono ? 15 : 19, fontWeight: mono ? 500 : 500, color: color ?? C.ink, whiteSpace: "nowrap" }}>
        {value}
        {unit && <span style={{ fontSize: 11, color: C.textLow, marginLeft: 3 }}>{unit}</span>}
      </div>
      <div className="eyebrow" style={{ marginTop: 2, fontSize: 9.5 }}>{label}</div>
    </div>
  );
}

/* ── Root ──────────────────────────────────────────────────────────────────*/
export function Schedule({ state }: { state: DashboardState }) {
  const schedule = state.schedule;
  const price = state.price;
  const reduced = useReducedMotion();
  const rootRef = useRef<HTMLDivElement>(null);
  const [hover, setHover] = useState<Hover | null>(null);

  const nowMin = useLiveNowMin(schedule?.nowMin ?? 0);

  const onShow = useCallback((el: HTMLElement, c: HoverContent) => {
    const r = el.getBoundingClientRect();
    const root = rootRef.current?.getBoundingClientRect();
    if (!root) return;
    setHover({ ...c, x: clamp(r.left - root.left + r.width / 2, 110, root.width - 110), top: r.top - root.top, bottom: r.bottom - root.top });
  }, []);
  const onHide = useCallback(() => setHover(null), []);

  // Escape dismisses the popover for keyboard users (it otherwise closes on blur/mouseleave)
  useEffect(() => {
    if (!hover) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setHover(null);
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [hover]);

  const shafts = useMemo(() => (schedule ? cheapColumns(schedule) : []), [schedule]);
  const groups = useMemo(() => (schedule ? groupZones(schedule.zones) : []), [schedule]);
  const liveById = useMemo(() => new Map(state.zones.map((z) => [z.id, z])), [state.zones]);

  // re-plan glow when the projection's shape changes
  const planHash = schedule ? schedule.blocks.map((b) => `${b.zoneId}:${b.startMin}:${b.durMin}:${b.kind}`).join("|") : "";
  const [glow, setGlow] = useState(false);
  const prevHash = useRef(planHash);
  useEffect(() => {
    if (prevHash.current !== planHash) {
      prevHash.current = planHash;
      if (!reduced) {
        setGlow(true);
        const t = setTimeout(() => setGlow(false), 1500);
        return () => clearTimeout(t);
      }
    }
  }, [planHash, reduced]);

  if (!schedule) {
    return (
      <div className="card-in" style={{ fontSize: 13, color: C.textMid }}>
        Timeline unavailable — no schedule in this snapshot.
      </div>
    );
  }

  const sc = makeScale(schedule.days || 1);
  const showPrice = !!price?.enabled && (schedule.priceBands.length > 0 || schedule.priceDays.some((d) => !d));

  return (
    <div ref={rootRef} className="card-in" style={{ position: "relative" }}>
      <HeroStrip state={state} reduced={reduced} />

      <div
        className={glow ? "replan-glow" : undefined}
        style={{
          background: C.paper, border: `1px solid ${C.line}`, borderRadius: 18, boxShadow: "var(--shadow-card)", padding: 20, overflowX: "auto",
          filter: state.paused ? "saturate(.6)" : undefined, transition: reduced ? undefined : "filter .6s ease",
        }}
      >
        <div style={{ minWidth: LABEL_W + sc.days * DAY_W, display: "flex", flexDirection: "column", gap: 4 }}>
          <DayAxis sc={sc} schedule={schedule} nowMin={nowMin} reduced={reduced} />

          <div style={{ position: "relative" }}>
            <OverlayWash sc={sc} schedule={schedule} shafts={shafts} paused={state.paused} reduced={reduced} hover={hover} />
            <OverlayLines sc={sc} schedule={schedule} nowMin={nowMin} hover={hover} paused={state.paused} />

            {showPrice ? (
              <PriceRow sc={sc} schedule={schedule} price={price} />
            ) : (
              price?.enabled && (
                <div style={{ display: "flex", alignItems: "center", gap: GAP, height: 28, position: "relative", zIndex: 1 }}>
                  <div style={{ width: LABEL_W, flexShrink: 0, fontSize: 12, color: C.textLow }}>Power · SE4</div>
                  <div style={{ flex: 1, fontSize: 11.5, color: C.textLow }}>Price unavailable — watering on weather alone.</div>
                </div>
              )
            )}

            <div style={{ height: 1, background: C.lineSoft, margin: "6px 0", position: "relative", zIndex: 1 }} />

            {groups.map((g) => (
              <div key={g.key}>
                <GroupHeader sc={sc} group={g} />
                {g.zones.map((z) => (
                  <SubLane key={z.id} sc={sc} schedule={schedule} shafts={shafts} zone={z} group={g} live={liveById.get(z.id)} paused={state.paused} reduced={reduced} onShow={onShow} onHide={onHide} />
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Rain-delay marker over the timeline body */}
      {state.paused && (
        <div style={{ position: "absolute", left: "50%", top: 200, transform: "translateX(-50%)", display: "inline-flex", alignItems: "center", gap: 7, padding: "8px 14px", borderRadius: 99, background: C.ink, color: C.paper, fontSize: 12.5, fontWeight: 600, boxShadow: "0 6px 18px -6px rgba(40,30,12,.5)", pointerEvents: "none", zIndex: 7 }}>
          <span className={reduced ? undefined : "beacon-pulse"} style={{ display: "inline-flex", borderRadius: 99 }}>
            <CloudRain size={14} />
          </span>
          Rain delay — plan held
        </div>
      )}

      {hover && <BlockPopover hover={hover} reduced={reduced} />}

      {/* Legend */}
      <div style={{ display: "flex", alignItems: "center", gap: 14, marginTop: 14, fontSize: 11.5, color: C.textMid, flexWrap: "wrap" }}>
        <LegendSwatch label="Watering now" bg={`linear-gradient(90deg, ${C.seaDeep}, ${C.ice})`} />
        <LegendSwatch label="Sprinkler" bg={C.sea} />
        <LegendSwatch label="Drip" bg={C.sage} />
        <LegendSwatch label="Projected" bg={hexA(C.sea, 0.5)} border={C.sea} dashed />
        <LegendSwatch label="Done" bg={hexA(C.textLow, 0.2)} border={hexA(C.textLow, 0.28)} />
        <span style={{ width: 1, height: 13, background: C.line }} />
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <span style={{ width: 14, borderTop: `2px solid ${hexA(C.beacon, 0.6)}`, height: 0 }} />
          <Sunrise size={12} style={{ color: C.beacon }} /> Sunrise
        </span>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <span style={{ width: 7, height: 14, borderRadius: 2, background: `linear-gradient(180deg, ${hexA(C.sageDeep, 0.02)}, ${hexA(C.sageDeep, 0.2)})`, borderLeft: `1px solid ${hexA(C.sageDeep, 0.3)}`, borderRight: `1px solid ${hexA(C.sageDeep, 0.3)}` }} /> Cheapest power
        </span>
        <span style={{ width: 1, height: 13, background: C.line }} />
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <Zap size={12} style={{ color: C.muted }} />
          <span style={{ display: "inline-flex", height: 11, width: 70, borderRadius: 3, background: `linear-gradient(90deg, ${C.sageDeep}, ${C.sea}, ${C.beacon}, ${C.coral})` }} />
          cheap → expensive
        </span>
      </div>
    </div>
  );
}

function LegendSwatch({ label, bg, border, dashed }: { label: string; bg: string; border?: string; dashed?: boolean }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
      <span style={{ width: 16, height: 11, borderRadius: 3, background: bg, border: border ? `1px ${dashed ? "dashed" : "solid"} ${border}` : "none" }} />
      {label}
    </span>
  );
}
